import { createModel } from 'ice';
import type { CurrentStatus, SeatInfo, Car, Team, CheckItem, CaseDetail, CaseEvent } from './types';
import { getCachedCurrentStatus, setCachedCurrentStatus, getCachedTeam, getCachedCar } from '@/utils/localStorage';

export interface MnState {
  /** 驾舱信息 */
  seat: SeatInfo | null;
  /** 当前绑定车辆 */
  car: Car | null;
  /** 当前选中车队 */
  team: Team | null;
  /** 可调度车辆列表 */
  carList: Car[];
  /** 自检项列表 */
  checkItems: CheckItem[];
  /** 当前 case 详情 */
  caseDetail: CaseDetail | null;
  /** 主流程状态 */
  currentStatus: CurrentStatus;
  /** 倒计时（秒） */
  countdownTime: number;
  /** case 事件列表 */
  caseEventList: CaseEvent[];
  /** 车辆列表搜索词 */
  searchTerm: string;
  /** 车辆列表状态筛选 */
  selectedStatus: string;
}

const _cachedTeam = getCachedTeam();

const initialState: MnState = {
  seat: null,
  car: getCachedCar(),
  team: _cachedTeam.team_id ? _cachedTeam as Team : null,
  carList: [],
  checkItems: [],
  caseDetail: null,
  currentStatus: (getCachedCurrentStatus() as CurrentStatus | null) ?? 'INIT_SCREEN',
  countdownTime: 0,
  caseEventList: [],
  searchTerm: '',
  selectedStatus: '',
};

export default createModel({
  state: initialState,

  reducers: {
    setSeat(prevState: MnState, payload: SeatInfo | null) {
      prevState.seat = payload;
    },

    setCar(prevState: MnState, payload: Car | null) {
      prevState.car = payload;
    },

    setTeam(prevState: MnState, payload: Team | null) {
      prevState.team = payload;
    },

    setCarList(prevState: MnState, payload: Car[]) {
      prevState.carList = payload;
    },

    setCheckItems(prevState: MnState, payload: CheckItem[]) {
      prevState.checkItems = payload;
    },

    updateCheckItem(prevState: MnState, payload: { id: string; status: CheckItem['status'] }) {
      const item = prevState.checkItems.find(c => c.id === payload.id);
      if (item) item.status = payload.status;
    },

    setCaseDetail(prevState: MnState, payload: CaseDetail | null) {
      prevState.caseDetail = payload;
    },

    setCurrentStatus(prevState: MnState, payload: CurrentStatus) {
      prevState.currentStatus = payload;
    },

    setCountdownTime(prevState: MnState, payload: number) {
      prevState.countdownTime = payload;
    },

    setCaseEventList(prevState: MnState, payload: CaseEvent[]) {
      prevState.caseEventList = payload;
    },

    appendCaseEvent(prevState: MnState, payload: CaseEvent) {
      prevState.caseEventList.push(payload);
    },

    setSearchTerm(prevState: MnState, payload: string) {
      prevState.searchTerm = payload;
    },

    setSelectedStatus(prevState: MnState, payload: string) {
      prevState.selectedStatus = payload;
    },

    /** 流程重置：清空 case / car 相关状态，回到初始屏 */
    resetFlow(prevState: MnState) {
      prevState.car = null;
      prevState.caseDetail = null;
      prevState.caseEventList = [];
      prevState.countdownTime = 0;
      prevState.currentStatus = 'INIT_SCREEN';
    },
  },

  effects: () => ({
    /** 切换状态并同步写缓存 */
    async switchStatus(status: CurrentStatus) {
      setCachedCurrentStatus(status);
      this.setCurrentStatus(status);
    },
  }),
});
